import type { AiProcessingProvenance } from '@clm/shared-types';

/**
 * What happened at the AI boundary for one contract — all five states, told
 * honestly.
 *
 * This is the component the engagement's compliance rules are most visible in,
 * and the switch below is exhaustive on the discriminated union so that a sixth
 * state added to `AiProcessingProvenanceSchema` is a **compile error** rather
 * than a blank panel. Three of the five have to read a particular way:
 *
 *  - **`refused`** must never render as pending. CLAUDE.md rule 6: a refusal
 *    routes to human review, never to an automatic fallback model, and the
 *    schema makes `routed_to_human_review` structurally `true`. Showing it as
 *    "still processing" would hide the one state a human has to act on.
 *  - **`excluded_processing_restricted`** is a deliberate policy state, not a
 *    failure (§4C, rule 4). The contract is stored, converted in-tenant,
 *    indexed and searchable; only the *reasoning* step is withheld, and its
 *    facts are entered by hand. It is rendered in the neutral/accent tone, and
 *    `restriction_source` says whether Legal decided it once for the party or
 *    overrode it for this document.
 *  - **`failed`** is distinct from `refused`. One is a broken pipeline, the
 *    other is a model declining; conflating them sends the wrong person to look.
 */
export function AiProcessingState({ ai }: { readonly ai: AiProcessingProvenance }) {
  switch (ai.ai_processing_state) {
    case 'pending':
      return (
        <div className="aist" data-state="pending">
          <span className="tag out">Extraction pending</span>
          <p>
            The pipeline has not produced facts for this contract yet. Nothing has been sent to a
            model, and no field below is AI-derived until it says so.
          </p>
        </div>
      );

    case 'excluded_processing_restricted':
      return (
        <div className="aist" data-state="excluded_processing_restricted">
          <span className="tag acc">Excluded from AI processing — by policy</span>
          <p>
            This contract&rsquo;s own terms forbid third-party processing, so no LLM has seen or
            will see its content (§4C). It is still stored, converted inside the tenant, indexed
            and searchable; its facts are entered by a person. This is a decision, not a failure,
            and it is not something to retry.
          </p>
          <dl className="prov">
            <dt>Restriction set</dt>
            <dd>
              {ai.restriction_source === 'party_inherited'
                ? 'Inherited from the party — Legal decides once per client'
                : 'Overridden for this contract'}
            </dd>
            <dt>Reason</dt>
            <dd>{ai.restriction_reason ?? 'No reason was recorded against the restriction.'}</dd>
          </dl>
        </div>
      );

    case 'completed':
      return (
        <div className="aist" data-state="completed">
          <span className="tag ok">Extraction completed</span>
          <dl className="prov">
            <dt>Extraction schema version</dt>
            <dd>{ai.extraction_schema_version}</dd>
            <dt>Provider</dt>
            <dd>{ai.provider_id}</dd>
            <dt>Model</dt>
            <dd>{ai.model}</dd>
            <dt>Extracted</dt>
            <dd>{ai.extracted_at_utc}</dd>
            <dt>Pseudonym map version</dt>
            <dd>{ai.pseudonym_map_version}</dd>
            <dt>Residual party names found before sending</dt>
            <dd>
              {String(ai.leak_detector.residual_hit_count)}
              {ai.leak_detector.send_blocked ? ' — the send was held for review' : ''}
              {ai.leak_detector.residual_terms.length === 0
                ? ''
                : ` (${ai.leak_detector.residual_terms.join(', ')})`}
            </dd>
            <dt>Document deleted at the processor</dt>
            <dd>Yes — evidenced (CR-02 control 4)</dd>
          </dl>
        </div>
      );

    case 'refused':
      return (
        <div className="aist" data-state="refused">
          <span className="tag bad">Refused by the model — routed to human review</span>
          <p>
            The model declined to extract from this contract. It has been routed to human review,
            which is the only route a refusal takes: re-serving it to a different model silently
            would break the CR-01 evidence chain, which has to show which model produced which
            field. Nothing here is awaiting the pipeline — it is awaiting a person.
          </p>
          <dl className="prov">
            <dt>Refusal category</dt>
            <dd>{ai.refusal_category ?? 'The provider gave no category.'}</dd>
            <dt>Refused</dt>
            <dd>{ai.refused_at_utc}</dd>
            <dt>Pseudonym map version</dt>
            <dd>{ai.pseudonym_map_version}</dd>
          </dl>
        </div>
      );

    case 'failed':
      return (
        <div className="aist" data-state="failed">
          <span className="tag bad">Extraction failed</span>
          <p>
            The extraction attempt did not complete. This is a pipeline failure rather than a model
            refusal — the two are separate states because they send different people to look.
          </p>
          <dl className="prov">
            <dt>Failure reason</dt>
            <dd>{ai.failure_reason}</dd>
            <dt>Failed</dt>
            <dd>{ai.failed_at_utc}</dd>
            <dt>Attempts</dt>
            <dd>{String(ai.attempt_count)}</dd>
          </dl>
        </div>
      );

    default:
      // Exhaustiveness guard. `never` here means every member of
      // AiProcessingProvenanceSchema is handled above; adding a sixth state to
      // the contract fails this assignment at compile time.
      return assertExhausted(ai);
  }
}

function assertExhausted(value: never): never {
  throw new Error(
    `Unhandled ai_processing_state: ${JSON.stringify(value)}. ` +
      'AiProcessingProvenanceSchema gained a member and AiProcessingState.tsx must render it.',
  );
}
